import * as React from "react"
import { cn } from "../../lib/utils"

const TOP_NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "guilds", label: "Guilds" },
  { key: "templates", label: "Templates" },
] as const

type TopNavItemKey = (typeof TOP_NAV_ITEMS)[number]["key"]

interface TopBarProps {
  activeItem: string
  onNavigate: (item: string) => void
  onRefresh?: () => void
  isRefreshing?: boolean
  disabledItems?: Set<string>
  className?: string
}

function TopBar({ activeItem, onNavigate, onRefresh, isRefreshing, disabledItems, className }: TopBarProps) {
  return (
    <nav
      className={cn(
        "bg-surface-container-low sticky top-0 z-50 flex justify-between items-center w-full px-6 py-3",
        className,
      )}
    >
      {/* Left: Brand + nav links */}
      <div className="flex items-center gap-6">
        <div className="text-lg font-black tracking-tighter text-on-surface uppercase">
          Role Bot
        </div>

        {/* Desktop nav links */}
        <div className="hidden md:flex gap-4">
          {TOP_NAV_ITEMS.map((item) => {
            const isActive = activeItem === item.key
            const isDisabled = disabledItems?.has(item.key)
            return (
              <button
                key={item.key}
                onClick={() => !isDisabled && onNavigate(item.key)}
                disabled={isDisabled}
                className={cn(
                  "text-sm font-medium transition-all duration-150 bg-transparent border-none",
                  isDisabled
                    ? "text-on-surface-variant/40 cursor-not-allowed"
                    : isActive
                      ? "text-primary-text border-b-2 border-primary pb-1 cursor-pointer"
                      : "text-on-surface-variant hover:text-on-surface cursor-pointer",
                )}
              >
                {item.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Right: Icons */}
      <div className="flex items-center gap-3">
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className={cn(
              "text-on-surface-variant hover:text-on-surface transition-colors duration-150 cursor-pointer bg-transparent border-none p-1.5 rounded-lg hover:bg-surface-container-high",
              isRefreshing && "opacity-50 cursor-not-allowed",
            )}
            aria-label="Refresh"
          >
            <span
              className={cn(
                "material-symbols-outlined text-[22px]",
                isRefreshing && "animate-spin",
              )}
            >
              refresh
            </span>
          </button>
        )}
        <button
          className="text-on-surface-variant hover:text-on-surface transition-colors duration-150 cursor-pointer bg-transparent border-none p-1.5 rounded-lg hover:bg-surface-container-high"
          aria-label="Account"
        >
          <span className="material-symbols-outlined text-[22px]">
            account_circle
          </span>
        </button>
      </div>
    </nav>
  )
}

export { TopBar, TOP_NAV_ITEMS }
export type { TopBarProps, TopNavItemKey }
